import { Alert, Button, Stack, Typography } from '@mui/material'
import { useCallback, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { AppleTripError, appleTripStart } from 'src/api/appleTrip'
import { GoogleTripError, googleTripStart } from 'src/api/googleTrip'
import { useInvitationGetQuery } from 'src/api/queries/invitations/useInvitationGetQuery/useInvitationGetQuery'
import { useConsoleSignInOptionsQuery } from 'src/api/queries/signIn/useConsoleSignInOptionsQuery/useConsoleSignInOptionsQuery'
import { teslaTripStart, teslaTripStartFailure } from 'src/api/teslaTrip'
import AppleSignInButton from 'src/components/AppleSignInButton'
import GoogleSignInButton from 'src/components/GoogleSignInButton'
import SignInOptions from 'src/components/SignInOptions'
import TeslaMark from 'src/components/TeslaMark'
import EntryLayout, {
  CONSOLE_STANDFIRST,
} from 'src/components/layout/EntryLayout'
import Page from 'src/components/layout/Page'
import { role } from 'src/format'
import LinkPasswordSetup from 'src/pages/PasswordResetPage/LinkPasswordSetup'
import { leaveFor } from 'src/utils/leaveFor'
import { invitationLinkTokenRead } from './invitationLinkToken'

// ----------------------------------------------------------------------
// Where an invitation email sends somebody.
//
// The email used to link to the sign-in page and say "Login or create a Tesla
// account", and the role had already been granted to whatever row held the
// address. Now the role waits for this page: the link proves the person reads
// mail at the invited address, and whichever account they sign in with gets
// the role, whatever address that account has.
// ----------------------------------------------------------------------

const Heading = ({ title, body }: { title: string; body: string }) => (
  <Stack spacing={1}>
    <Typography variant="h2">{title}</Typography>
    <Typography variant="body2" color="text.secondary">
      {body}
    </Typography>
  </Stack>
)

const BackToSignIn = () => (
  <Button component={RouterLink} to="/login" variant="outlined" size="large">
    Go to sign in
  </Button>
)

const InvitationBody = () => {
  const [token] = useState(invitationLinkTokenRead)
  const { data, loading, error } = useInvitationGetQuery(token)
  const [leaving, setLeaving] = useState(false)
  const [withPassword, setWithPassword] = useState(false)
  const [startError, setStartError] = useState<string>()

  // The invitation rides in the trip the backend keeps, not in the URL Tesla
  // sends back, so a return cannot be handed somebody else's invitation.
  const continueWithTesla = useCallback(async () => {
    setLeaving(true)
    setStartError(undefined)
    try {
      leaveFor(
        await teslaTripStart({ purpose: 'sign_in', invitationToken: token }),
      )
    } catch (failure) {
      setStartError(teslaTripStartFailure(failure))
      setLeaving(false)
    }
  }, [token])

  const offered = useConsoleSignInOptionsQuery().data?.consoleSignInOptions
  const googleOffered = offered?.google === true
  const appleOffered = offered?.apple === true

  // The invitation rides in Google's trip the same way.
  const continueWithGoogle = useCallback(async () => {
    setLeaving(true)
    setStartError(undefined)
    try {
      leaveFor(
        await googleTripStart({ purpose: 'sign_in', invitationToken: token }),
      )
    } catch (failure) {
      setStartError(
        failure instanceof GoogleTripError
          ? failure.message
          : 'Google sign in could not start. Check your connection and try again.',
      )
      setLeaving(false)
    }
  }, [token])

  // And in Apple's.
  const continueWithApple = useCallback(async () => {
    setLeaving(true)
    setStartError(undefined)
    try {
      leaveFor(
        await appleTripStart({ purpose: 'sign_in', invitationToken: token }),
      )
    } catch (failure) {
      setStartError(
        failure instanceof AppleTripError
          ? failure.message
          : 'Apple sign in could not start. Check your connection and try again.',
      )
      setLeaving(false)
    }
  }, [token])

  if (!token) {
    return (
      <Stack spacing={3}>
        <Heading
          title="This link is incomplete"
          body="Open the invitation again from the email, using its button. Copying the address by hand can leave part of it behind."
        />
        <BackToSignIn />
      </Stack>
    )
  }

  if (loading) {
    return <Heading title="Opening your invitation" body="One moment." />
  }

  const invitation = data?.invitationGet

  if (error || !invitation) {
    return (
      <Stack spacing={3}>
        <Heading
          title="The invitation could not be opened"
          body="Check your connection and open the link again."
        />
        <Alert severity="error" role="alert">
          {error?.message ?? 'The invitation could not be read.'}
        </Alert>
      </Stack>
    )
  }

  switch (invitation.state) {
    case 'pending':
      if (withPassword && invitation.email) {
        return (
          <LinkPasswordSetup
            link={{ invitationToken: token }}
            email={invitation.email}
            onBack={() => setWithPassword(false)}
          />
        )
      }
      return (
        <Stack spacing={3}>
          <Heading
            title={`Join ${invitation.providerName ?? 'the team'}`}
            body={`You are invited as ${
              role(invitation.role) ?? 'a team member'
            }. Sign in with any Tesla account to accept: the role goes to the account you sign in with, even if its address is not ${
              invitation.email ?? 'the one invited'
            }. Or set a password, to sign in with this address instead.`}
          />
          {startError && (
            <Alert severity="error" role="alert">
              {startError}
            </Alert>
          )}
          <SignInOptions>
            <Button
              variant="contained"
              onClick={continueWithTesla}
              color="tesla"
              disabled={leaving}
              size="large"
              fullWidth
              startIcon={<TeslaMark inherit />}
            >
              Continue with Tesla
            </Button>
            {googleOffered && (
              <GoogleSignInButton
                size="large"
                fullWidth
                disabled={leaving}
                onClick={() => void continueWithGoogle()}
              />
            )}
            {appleOffered && (
              <AppleSignInButton
                size="large"
                fullWidth
                disabled={leaving}
                onClick={() => void continueWithApple()}
              />
            )}
          </SignInOptions>
          {/* A password makes a new console account for the invited
              address, so it is offered only for that address: somebody who
              already has an account signs in the way they usually do. */}
          <Button
            variant="outlined"
            size="large"
            disabled={leaving}
            onClick={() => setWithPassword(true)}
            fullWidth
          >
            Set a password instead
          </Button>
        </Stack>
      )
    case 'expired':
      return (
        <Stack spacing={3}>
          <Heading
            title="This invitation has expired"
            body={`Invitations work for a week. Ask ${
              invitation.providerName ?? 'whoever invited you'
            } to send it again.`}
          />
          <BackToSignIn />
        </Stack>
      )
    case 'accepted':
      return (
        <Stack spacing={3}>
          <Heading
            title="This invitation has been used"
            body="Sign in the way you did when you accepted it."
          />
          <BackToSignIn />
        </Stack>
      )
    case 'revoked':
      return (
        <Stack spacing={3}>
          <Heading
            title="This invitation was withdrawn"
            body={`${
              invitation.providerName ?? 'The team'
            } withdrew it. Ask them if you think it should still stand.`}
          />
          <BackToSignIn />
        </Stack>
      )
    default:
      return (
        <Stack spacing={3}>
          <Heading
            title="This link no longer works"
            body="It may have been replaced by a newer invitation. Use the latest email, or ask for the invitation to be sent again."
          />
          <BackToSignIn />
        </Stack>
      )
  }
}

const InvitationPage = () => (
  <Page title="Invitation">
    <EntryLayout
      headline="Join your team."
      standfirst={CONSOLE_STANDFIRST}
      photos
    >
      <InvitationBody />
    </EntryLayout>
  </Page>
)

export default InvitationPage
